/**
 * Direct deep-link screen for retrying one existing Toolbench property-value run.
 */
import { escapeHtml } from "./dashboard-html.js";
import { authenticatedFetch } from "./dashboard-auth.js";
import type { ToolbenchRun } from "./dashboard-lead-factory-types.js";

export function directToolbenchRetryRunId(): string {
  return new URLSearchParams(window.location.search).get("toolbench_retry_run")?.trim() ?? "";
}

export async function renderDirectToolbenchRetry(mount: HTMLElement, runId: string): Promise<void> {
  mount.innerHTML = `
    <main class="dashboard-shell sp-lookup-direct" data-toolbench-retry-direct>
      <section class="sp-lookup-hero">
        <div>
          <p class="eyebrow">Operator action</p>
          <h1>Retry existing Toolbench run</h1>
          <p>This action queues exactly one existing property-value run through the backend retry path. It does not create a new cache identity.</p>
        </div>
        <div class="sp-lookup-hero-actions">
          <a class="button secondary" href="/">Dashboard</a>
          <a class="button secondary" href="/#research-tools">Toolbench</a>
        </div>
      </section>
      <section class="panel" data-toolbench-retry-details>
        <p class="subtle">Run ID: <code>${escapeHtml(runId)}</code></p>
        <div data-toolbench-retry-summary></div>
        <button class="button primary" type="button" data-toolbench-retry-submit disabled>Queue this existing run</button>
        <p class="action-status" role="status" aria-live="polite" data-toolbench-retry-status>Loading the existing Toolbench run...</p>
      </section>
    </main>
  `;
  const details = document.querySelector<HTMLElement>("[data-toolbench-retry-summary]");
  const button = document.querySelector<HTMLButtonElement>("[data-toolbench-retry-submit]");
  const status = document.querySelector<HTMLElement>("[data-toolbench-retry-status]");
  if (!details || !button || !status) return;

  let run: ToolbenchRun;
  try {
    const response = await authenticatedFetch(`/dashboard/toolbench/runs/${encodeURIComponent(runId)}`);
    const body = (await response.json().catch(() => ({}))) as Partial<{ run: ToolbenchRun; error: string; message: string }>;
    if (!response.ok || !body.run) throw new Error(body.message || body.error || `Run read failed with ${response.status}`);
    run = body.run;
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : "The existing run could not be loaded.";
    return;
  }

  const inputAddress = typeof run.input?.fullAddress === "string" ? run.input.fullAddress : "";
  const requestedAddress = run.result?.requested_address;
  const resultAddress = requestedAddress && typeof requestedAddress === "object" && !Array.isArray(requestedAddress)
    ? String((requestedAddress as Record<string, unknown>).display ?? "")
    : "";
  const address = inputAddress || resultAddress;
  details.innerHTML = `
    <dl class="sp-assumption-card">
      <div><dt>Tool</dt><dd>${escapeHtml(run.tool_label || run.tool_id)}</dd></div>
      <div><dt>Status</dt><dd>${escapeHtml(run.status)}</dd></div>
      <div><dt>Failure class</dt><dd>${escapeHtml(run.failure_class || "none")}</dd></div>
      <div><dt>Address</dt><dd>${escapeHtml(address || "not available")}</dd></div>
    </dl>
  `;
  if (run.tool_id !== "property_value_lookup" || !["needs_review", "failed"].includes(run.status)) {
    status.textContent = "This run is not an eligible property-value retry target.";
    return;
  }
  button.disabled = false;
  button.addEventListener("click", () => {
    button.disabled = true;
    void retryDirectToolbenchRun(runId, status, button);
  });
}

export async function retryDirectToolbenchRun(runId: string, status: HTMLElement, button: HTMLButtonElement): Promise<void> {
  status.textContent = "Queueing the existing run...";
  try {
    const response = await authenticatedFetch(`/dashboard/toolbench/runs/${encodeURIComponent(runId)}/sp-retry`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ reason: "Zillow street-format discovery fix canary." })
    });
    const body = (await response.json().catch(() => ({}))) as Partial<{ run: ToolbenchRun; error: string; message: string }>;
    if (!response.ok || !body.run) throw new Error(body.message || body.error || `Retry failed with ${response.status}`);
    status.textContent = `Existing run queued: ${body.run.id}. Waiting for a terminal result...`;
    await pollDirectToolbenchRun(body.run.id, status);
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : "The existing run could not be queued.";
    button.disabled = false;
  }
}

export async function pollDirectToolbenchRun(runId: string, status: HTMLElement): Promise<void> {
  for (let attempt = 0; attempt < 180; attempt += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, 2_000));
    const response = await authenticatedFetch(`/dashboard/toolbench/runs/${encodeURIComponent(runId)}`);
    const body = (await response.json().catch(() => ({}))) as Partial<{ run: ToolbenchRun; error: string; message: string }>;
    if (!response.ok || !body.run) throw new Error(body.message || body.error || `Run read failed with ${response.status}`);
    const run = body.run;
    if (["queued", "running", "retry_wait"].includes(run.status)) {
      status.textContent = `Run ${run.id} is ${run.status}; waiting for the browser worker...`;
      continue;
    }
    status.textContent = `Run ${run.id} finished as ${run.status}${run.failure_class ? ` (${run.failure_class})` : "."}`;
    return;
  }
  throw new Error("The existing run did not reach a terminal state within the bounded canary wait.");
}
